// ================================
// CUSTOMER DASHBOARD - PHASE 2
// Real-time wallet, active order banner, nav to wallet
// ================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { isPushSupported, getExistingSubscription, enablePushNotifications } from '../../utils/pushNotifications';
import ChatThread from '../../components/shared/ChatThread';
import InstallPrompt from '../../components/shared/InstallPrompt';
import useTelegramConnect from '../../utils/useTelegramConnect';

export default function CustomerDashboard() {
  const { user, logout }      = useAuth();
  const socket                = useSocket();
  const navigate              = useNavigate();
  const [member, setMember]   = useState(user);
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [rate, setRate]       = useState(null);
  const [showQR, setShowQR]         = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({ address: '', zip: '' });
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressError, setAddressError]   = useState('');
  const [showPushBanner, setShowPushBanner] = useState(false);
  const [enablingPush, setEnablingPush]     = useState(false);
  const [pushError, setPushError]           = useState('');
  const [livePromos, setLivePromos]           = useState([]);
  const [dismissedPromoIds, setDismissedPromoIds] = useState([]);
  const [storyPromo, setStoryPromo]           = useState(null);
  const [showSupport, setShowSupport]         = useState(false);
  const [showCheckin, setShowCheckin]         = useState(false);
  const [checkinStaffName, setCheckinStaffName] = useState('');
  const [unread, setUnread] = useState({ directThread: null, orderThreads: {}, totalUnread: 0 });
  const [deliveryCode, setDeliveryCode]     = useState(null);
  const [codeExpiresAt, setCodeExpiresAt]   = useState(null);
  const [productTypes, setProductTypes]     = useState([]);
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const telegram = useTelegramConnect('customer');

  useEffect(() => {
    fetchData();
    fetchRate();
    checkPushStatus();
    fetchLivePromos();
    fetchUnread();
    fetchProductTypes();
    fetchPendingPurchase();
  }, []);

  // A generated Cash App link used to live only in the Wallet page's own
  // component state — leave that screen before paying (or before an admin
  // confirms it) and there was no trace of it anywhere else, no banner, no
  // way back to it short of re-entering the same coin amount from scratch.
  async function fetchPendingPurchase() {
    try {
      const res = await axios.get('/api/coins/my-pending-purchase');
      setPendingPurchase(res.data.request || null);
    } catch (e) {}
  }

  // "Shop the Menu" tiles are product types (Flower, Edibles, Carts, ...) —
  // the real top level of the menu. Cheapies/Mids/ZaZa/Gas are quality
  // tiers *within* a type, not a peer of it, so they live one level down
  // inside the order builder instead of here. Only types that actually
  // have an active item show up, so an empty type (e.g. Edibles before
  // any are added) doesn't show as a dead end.
  async function fetchProductTypes() {
    try {
      const res = await axios.get('/api/menu');
      const types = [...new Set((res.data.menu || []).map(i => i.productType || 'Flower'))];
      setProductTypes(types);
    } catch (e) {}
  }

  async function fetchUnread() {
    try {
      const res = await axios.get('/api/messages/my-unread');
      setUnread(res.data);
    } catch (e) {}
  }

  // Grabs the signing staffer's display name before opening the thread so
  // the panel can be titled "Message Jordan" instead of something generic —
  // ChatThread itself does its own fetch for the actual messages.
  async function openCheckin() {
    try {
      const res = await axios.get('/api/messages/my-checkin');
      setCheckinStaffName(res.data.staffName || 'Your Rep');
    } catch (e) { setCheckinStaffName('Your Rep'); }
    setShowCheckin(true);
  }

  async function fetchLivePromos() {
    try {
      const res = await axios.get('/api/promos/live');
      setLivePromos(res.data.promos || []);
    } catch (e) {}
  }

  async function checkPushStatus() {
    if (!isPushSupported() || Notification.permission === 'denied') return;
    const existing = await getExistingSubscription().catch(() => null);
    if (!existing) setShowPushBanner(true);
  }

  async function handleEnablePush() {
    setEnablingPush(true);
    setPushError('');
    try {
      await enablePushNotifications();
      setShowPushBanner(false);
    } catch (e) {
      setPushError(e.message || 'Could not enable notifications');
    } finally {
      setEnablingPush(false);
    }
  }

  useEffect(() => {
    if (!socket) return;
    socket.on('order:delivered', () => fetchData());
    socket.on('order:updated',   () => fetchData());
    socket.on('wallet:updated',  ({ balance }) => setMember(m => ({ ...m, wallet: balance })));
    socket.on('rate:updated',    ({ rate: r }) => setRate(r));
    socket.on('promos:updated',  () => fetchLivePromos());
    socket.on('message:new',     () => fetchUnread());
    socket.on('coin:purchase-approved',  () => setPendingPurchase(null));
    socket.on('coin:purchase-rejected',  () => setPendingPurchase(null));
    return () => {
      socket.off('order:delivered');
      socket.off('order:updated');
      socket.off('wallet:updated');
      socket.off('rate:updated');
      socket.off('promos:updated');
      socket.off('message:new');
      socket.off('coin:purchase-approved');
      socket.off('coin:purchase-rejected');
    };
  }, [socket]);

  async function fetchData() {
    try {
      const [memberRes, ordersRes] = await Promise.all([
        axios.get('/api/members/me'),
        axios.get('/api/orders/my-orders')
      ]);
      setMember(memberRes.data.member);
      setOrders(ordersRes.data.orders || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  const activeOrder = orders.find(o => o.status !== 'Delivered' && o.status !== 'Attempted Delivery' && o.status !== 'Cancelled');

  // Fetches the delivery confirmation code once the driver has arrived,
  // and silently refreshes it ~30s before it would expire so the customer
  // never has to unlock their phone again mid-handoff.
  useEffect(() => {
    if (activeOrder?.status !== 'Driver Arrived') {
      setDeliveryCode(null);
      setCodeExpiresAt(null);
      return;
    }
    let timer;
    async function fetchCode() {
      try {
        const res = await axios.get(`/api/orders/${activeOrder.oid}/delivery-code`);
        setDeliveryCode(res.data.code);
        setCodeExpiresAt(res.data.expiresAt);
        const refreshIn = Math.max(res.data.expiresAt - Date.now() - 30000, 5000);
        timer = setTimeout(fetchCode, refreshIn);
      } catch (e) { console.error(e); }
    }
    fetchCode();
    return () => clearTimeout(timer);
  }, [activeOrder?.oid, activeOrder?.status]);

  async function fetchRate() {
    try {
      const res = await axios.get('/api/coins/rate');
      setRate(res.data.rate);
    } catch(e) {}
  }

  function openAddressForm() {
    setAddressForm({ address: member?.address || '', zip: member?.zip || '' });
    setAddressError('');
    setShowAddress(true);
  }

  async function saveAddress(e) {
    e.preventDefault();
    setAddressSaving(true);
    setAddressError('');
    try {
      await axios.put(`/api/members/${member.mid}/address`, addressForm);
      setMember(m => ({ ...m, address: addressForm.address, zip: addressForm.zip }));
      setShowAddress(false);
    } catch(e) {
      setAddressError(e.response?.data?.error || 'Failed to update address');
    } finally {
      setAddressSaving(false);
    }
  }

  const balance = parseFloat(member?.wallet || 0);

  const typeMeta = {
    Flower:      { icon:'🌿', sub:'Buds, by the gram' },
    Edibles:     { icon:'🍬', sub:'Gummies & more' },
    Tinctures:   { icon:'💧', sub:'Drops & oils' },
    Carts:       { icon:'🔋', sub:'Vape cartridges' },
    Concentrates:{ icon:'🍯', sub:'Dabs & extracts' },
    Other:       { icon:'📦', sub:'Everything else' },
  };

  return (
    <div className="app-shell">

      {/* Header */}
      <div style={{ padding:'20px 22px 14px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:2 }}>Good evening,</p>
          <h2 style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:26, letterSpacing:2 }}>
            {member?.name?.split(' ')[0]} 👋
          </h2>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={telegram.openOrReopen}
            title={telegram.linked ? 'Open Telegram' : 'Connect Telegram'}
            style={{ background:'#0a1530', color:'var(--accent-blue)', border:'1px solid var(--border-hover)', borderRadius:20, fontSize:14, padding:'6px 10px', cursor:'pointer' }}>
            ✈️
          </button>
          <button onClick={logout} title="Sign Out"
            style={{ background:'#0a1530', color:'var(--text-secondary)', border:'1px solid var(--border-hover)', borderRadius:20, fontSize:14, padding:'6px 10px', cursor:'pointer' }}>
            ↩️
          </button>
        </div>
      </div>

      <InstallPrompt />

      {/* Live promo feed — horizontal strip, each dismissible on its own */}
      {livePromos.filter(p => !dismissedPromoIds.includes(p.id)).length > 0 && (
        <div style={{ margin:'12px 0 0' }}>
          <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, padding:'0 20px', marginBottom:8 }}>📢 From Weedo World</p>
          <div style={{ display:'flex', gap:10, overflowX:'auto', padding:'0 20px 4px', scrollSnapType:'x proximity' }}>
            {livePromos.filter(p => !dismissedPromoIds.includes(p.id)).map(p => (
              <div key={p.id} style={{
                flex:'0 0 240px', scrollSnapAlign:'start',
                background:'linear-gradient(135deg, #1a0a05, #0a1530)',
                border:'1px solid #c0101a40', borderRadius:'var(--radius-lg)',
                overflow:'hidden', position:'relative'
              }}>
                {p.imageUrl && (
                  <img src={p.imageUrl} alt="" style={{ width:'100%', height:100, objectFit:'cover', display:'block' }} />
                )}
                <div style={{ padding:'12px 14px' }}>
                  <button onClick={() => setDismissedPromoIds(ids => [...ids, p.id])} style={{
                    position:'absolute', top:8, right:10, background:'none', border:'none',
                    color:'#fff', fontSize:16, cursor:'pointer', lineHeight:1, textShadow:'0 1px 3px #000'
                  }}>×</button>
                  <p style={{ fontSize:13, color:'var(--text-primary)', fontWeight:500, marginBottom:4, paddingRight:16 }}>{p.title}</p>
                  {p.description && <p style={{ fontSize:11, color:'var(--text-secondary)', lineHeight:1.4 }}>{p.description}</p>}
                  {p.story && (
                    <button onClick={() => setStoryPromo(p)} style={{
                      marginTop:8, background:'none', border:'none', padding:0,
                      color:'var(--accent-blue)', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif'
                    }}>
                      Read the story →
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Promo story modal */}
      {storyPromo && (
        <div style={{ position:'fixed', inset:0, background:'#000000cc', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50 }}
          onClick={() => setStoryPromo(null)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg-card)', border:'1px solid var(--border-hover)', borderRadius:20, width:'88%', maxWidth:380, maxHeight:'80vh', overflowY:'auto' }}>
            {storyPromo.imageUrl && <img src={storyPromo.imageUrl} alt="" style={{ width:'100%', height:160, objectFit:'cover', display:'block' }} />}
            <div style={{ padding:22 }}>
              <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:22, letterSpacing:1, marginBottom:10 }}>{storyPromo.title}</p>
              <p style={{ fontSize:13, color:'var(--text-primary)', lineHeight:1.6, whiteSpace:'pre-wrap', marginBottom:18 }}>{storyPromo.story}</p>
              <button className="btn-secondary" onClick={()=>setStoryPromo(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Push notification opt-in */}
      {showPushBanner && (
        <div style={{
          margin:'12px 20px 0', background:'var(--bg-card)',
          border:'1px solid var(--border)', borderRadius:'var(--radius-lg)',
          padding:'14px 16px'
        }}>
          <p style={{ fontSize:13, color:'var(--text-primary)', marginBottom:4 }}>🔔 Turn on order updates</p>
          <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:10 }}>
            Get notified here when your order is confirmed, your driver's on the way, and it's delivered.
          </p>
          {pushError && <p style={{ fontSize:11, color:'var(--accent-red)', marginBottom:8 }}>{pushError}</p>}
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={handleEnablePush} disabled={enablingPush} style={{
              flex:1, padding:'8px 0', borderRadius:'var(--radius)', border:'none',
              background:'var(--accent-blue)', color:'#fff', fontSize:13, cursor:'pointer'
            }}>
              {enablingPush ? 'Enabling…' : 'Enable notifications'}
            </button>
            <button onClick={() => setShowPushBanner(false)} style={{
              padding:'8px 14px', borderRadius:'var(--radius)', border:'1px solid var(--border)',
              background:'transparent', color:'var(--text-secondary)', fontSize:13, cursor:'pointer'
            }}>
              Not now
            </button>
          </div>
        </div>
      )}

      {/* Telegram opt-in */}
      {telegram.linked === false && !telegram.dismissed && (
        <div style={{
          margin:'12px 20px 0', background:'var(--bg-card)',
          border:'1px solid var(--border)', borderRadius:'var(--radius-lg)',
          padding:'14px 16px', position:'relative'
        }}>
          <button onClick={telegram.dismiss} aria-label="Dismiss"
            style={{ position:'absolute', top:10, right:10, background:'none', border:'none', color:'var(--text-muted)', fontSize:16, cursor:'pointer', lineHeight:1 }}>
            ✕
          </button>
          <p style={{ fontSize:13, color:'var(--text-primary)', marginBottom:4, paddingRight:20 }}>✈️ Connect Telegram</p>
          <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:10 }}>
            Get order updates as Telegram messages too — handy if you don't want to rely on browser notifications alone.
          </p>
          {telegram.code && (
            <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:10 }}>
              Open Telegram, message the bot, and send: <strong>/start {telegram.code}</strong>
            </p>
          )}
          <button onClick={telegram.connect} disabled={telegram.connecting} style={{
            padding:'8px 16px', borderRadius:'var(--radius)', border:'none',
            background:'#229ED9', color:'#fff', fontSize:13, cursor:'pointer'
          }}>
            {telegram.connecting ? 'Waiting for you to connect…' : 'Connect Telegram'}
          </button>
        </div>
      )}
      {telegram.linked === true && (
        <p style={{ margin:'10px 20px 0', fontSize:11, color:'var(--text-muted)' }}>✈️ Telegram connected</p>
      )}

      {/* Active order banner */}
      {activeOrder && (
        <div style={{
          margin:'12px 20px 0', background:'#0a1530',
          border:'1px solid #1e3a6a', borderRadius:'var(--radius-lg)',
          padding:'12px 16px'
        }}>
          <div onClick={() => navigate('/track')} style={{ cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginBottom:2 }}>Active Order</p>
              <p style={{ fontSize:14, color:'var(--text-primary)' }}>{activeOrder.oid} — {activeOrder.summary}</p>
              <p style={{ fontSize:12, color:'var(--accent-blue)', marginTop:2 }}>{activeOrder.status}</p>
            </div>
            <span style={{ color:'var(--accent-blue)', fontSize:18 }}>→</span>
          </div>
          {deliveryCode && (
            <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #1e3a6a', textAlign:'center' }}>
              <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>
                Give your driver this code
              </p>
              <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:36, color:'var(--accent-blue)', letterSpacing:6 }}>
                {deliveryCode}
              </p>
              <p style={{ fontSize:10, color:'var(--text-muted)', marginTop:2 }}>
                Read it aloud, or your driver can scan your member card instead
              </p>
            </div>
          )}
        </div>
      )}

      {/* Pending coin purchase banner — a generated Cash App link/quote is
          locked server-side the moment it's created, but a customer who
          leaves the Wallet page with nothing yet paid had no way back to
          it and no reminder it was still sitting there unpaid. */}
      {pendingPurchase && (
        <div onClick={() => navigate('/wallet')} style={{
          margin:'12px 20px 0', background:'#0a1530',
          border:'1px solid #1e3a6a', borderRadius:'var(--radius-lg)',
          padding:'12px 16px', cursor:'pointer',
          display:'flex', justifyContent:'space-between', alignItems:'center'
        }}>
          <div>
            <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginBottom:2 }}>Coin Purchase Pending</p>
            <p style={{ fontSize:14, color:'var(--text-primary)' }}>{pendingPurchase.coinAmount} coins — ${pendingPurchase.total}</p>
            <p style={{ fontSize:12, color:'var(--accent-blue)', marginTop:2 }}>Awaiting payment confirmation</p>
          </div>
          <span style={{ color:'var(--accent-blue)', fontSize:18 }}>→</span>
        </div>
      )}

      {/* Wallet Card */}
      <div style={{
        margin:'16px 20px', background:'var(--bg-card)',
        border:'1px solid var(--border)', borderRadius:18, padding:20,
        position:'relative', overflow:'hidden', cursor:'pointer'
      }} onClick={() => navigate('/wallet')}>
        <div style={{ position:'absolute', top:-20, right:-20, width:120, height:120, background:'radial-gradient(circle, #1e6bff12, transparent 70%)', pointerEvents:'none' }} />
        <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Coin Balance</p>
        <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:40, color:'var(--accent-blue)', letterSpacing:1 }}>
          {balance.toFixed(0)} <span style={{ fontSize:18, color:'var(--text-secondary)' }}>coins</span>
        </p>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8 }}>
          <p style={{ fontSize:11, color:'var(--text-muted)' }}>M.I.D. • {member?.mid} • Private Member</p>
          {rate && <p style={{ fontSize:11, color:'var(--text-secondary)' }}>Rate: ${rate}/coin</p>}
        </div>
        <p style={{ fontSize:11, color:'var(--accent-blue)', marginTop:6 }}>Tap to buy coins or send →</p>
      </div>

      {/* Account actions */}
      <div style={{ display:'flex', gap:10, padding:'0 20px', marginBottom:20 }}>
        <button onClick={()=>setShowQR(true)} style={{ flex:1, padding:12, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, color:'var(--text-primary)', fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
          🪪 My QR Card
        </button>
        <button onClick={openAddressForm} style={{ flex:1, padding:12, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, color:'var(--text-primary)', fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
          📍 Update Address
        </button>
        <button onClick={()=>setShowSupport(true)} style={{ position:'relative', flex:1, padding:12, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, color:'var(--text-primary)', fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
          💬 Contact Support
          {unread.directThread?.unreadCount > 0 && (
            <span style={{ position:'absolute', top:-6, right:-6, background:'var(--accent-red)', color:'#fff', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontFamily:'DM Sans,sans-serif' }}>
              {unread.directThread.unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Only shown if a staffer actually signed this member up in the
          field — a direct line to them for routine check-ins, separate
          from general Contact Support above. */}
      {member?.signedUpByStaff && (
        <button onClick={openCheckin} style={{ position:'relative', width:'100%', padding:12, marginBottom:16, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, color:'var(--text-primary)', fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
          👋 Message Your Rep
          {unread.checkinUnread > 0 && (
            <span style={{ position:'absolute', top:-6, right:-6, background:'var(--accent-red)', color:'#fff', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontFamily:'DM Sans,sans-serif' }}>
              {unread.checkinUnread}
            </span>
          )}
        </button>
      )}

      {showSupport && member?.mid && (
        <ChatThread
          title="Message Weedo World Support"
          selfRole="customer"
          fetchUrl={`/api/messages/thread/customer/${member.mid}`}
          postUrl={`/api/messages/thread/customer/${member.mid}`}
          onClose={() => { setShowSupport(false); fetchUnread(); }}
        />
      )}

      {showCheckin && (
        <ChatThread
          title={`Message ${checkinStaffName}`}
          selfRole="customer"
          fetchUrl="/api/messages/my-checkin"
          postUrl="/api/messages/my-checkin"
          onClose={() => { setShowCheckin(false); fetchUnread(); }}
        />
      )}

      {/* QR Card Modal */}
      {showQR && (
        <div style={{ position:'fixed', inset:0, background:'#000000cc', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50 }}
          onClick={()=>setShowQR(false)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg-card)', border:'1px solid var(--border-hover)', borderRadius:20, padding:28, width:'85%', maxWidth:320, textAlign:'center' }}>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:20, letterSpacing:2, marginBottom:16 }}>Your Member Card</p>
            <div style={{ background:'#fff', borderRadius:14, padding:20, display:'inline-block', marginBottom:16 }}>
              <QRCodeSVG value={`${window.location.origin}/c/${member?.mid}`} size={200} />
            </div>
            <p style={{ fontSize:13, color:'var(--text-primary)', marginBottom:4 }}>{member?.name}</p>
            <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:16 }}>M.I.D. {member?.mid}</p>
            <p style={{ fontSize:11, color:'var(--text-secondary)', lineHeight:1.5, marginBottom:16 }}>Show this to your driver when they arrive — scanning it confirms delivery. Anyone else who scans it (with any phone camera) just gets taken to sign in.</p>
            <button className="btn-secondary" onClick={()=>setShowQR(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Update Address Modal */}
      {showAddress && (
        <div style={{ position:'fixed', inset:0, background:'#000000cc', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50 }}
          onClick={()=>setShowAddress(false)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg-card)', border:'1px solid var(--border-hover)', borderRadius:20, padding:28, width:'85%', maxWidth:340 }}>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:20, letterSpacing:2, marginBottom:16 }}>Update Delivery Address</p>
            <form onSubmit={saveAddress}>
              <div className="field">
                <label>Street Address</label>
                <input type="text" value={addressForm.address} onChange={e=>setAddressForm(f=>({...f, address:e.target.value}))} required />
              </div>
              <div className="field">
                <label>Zip Code</label>
                <input type="text" value={addressForm.zip} onChange={e=>setAddressForm(f=>({...f, zip:e.target.value}))} required />
              </div>
              {addressError && <p style={{ fontSize:12, color:'var(--accent-red)', marginBottom:12 }}>{addressError}</p>}
              <button className="btn-primary" type="submit" disabled={addressSaving}>{addressSaving ? 'Verifying address...' : 'Save Address'}</button>
              <button type="button" onClick={()=>setShowAddress(false)} style={{ display:'block', width:'100%', marginTop:10, background:'none', border:'none', color:'var(--text-secondary)', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {/* Shop Menu */}
      <p className="section-label" style={{ padding:'0 22px 10px' }}>Shop the Menu</p>
      <div style={{ padding:'0 20px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
        {productTypes.map(type => (
          <div key={type}
            onClick={() => navigate('/order', { state:{ productType: type } })}
            style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:14, cursor:'pointer', transition:'border-color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor='#1e6bff40'}
            onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}
          >
            <div style={{ fontSize:28, marginBottom:8 }}>{(typeMeta[type] || typeMeta.Other).icon}</div>
            <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:17, letterSpacing:1 }}>{type}</p>
            <p style={{ fontSize:11, color:'var(--accent-blue)', marginTop:2 }}>{(typeMeta[type] || typeMeta.Other).sub}</p>
          </div>
        ))}
      </div>

      <div style={{ flex:1 }} />

      {/* Nav Bar */}
      <div className="nav-bar">
        <button className="nav-item active"><span style={{ fontSize:17 }}>🏠</span>Home</button>
        <button className="nav-item" onClick={() => navigate('/order')}><span style={{ fontSize:17 }}>🛒</span>Order</button>
        <button className="nav-item" onClick={() => navigate('/wallet')}><span style={{ fontSize:17 }}>💰</span>Wallet</button>
        <button className="nav-item" onClick={() => navigate('/rewards')}><span style={{ fontSize:17 }}>🏆</span>Rewards</button>
        <button className="nav-item" onClick={() => navigate('/track')}><span style={{ fontSize:17 }}>🕐</span>Track</button>
      </div>
    </div>
  );
}
